import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Input,
  Spinner,
  Checkbox,
  Tooltip,
  Table,
  TableHeader,
  TableRow,
  TableColumn,
  TableBody,
  TableCell,
} from "@heroui/react";
import {
  EditableInputComponent,
  EditableSelectComponent,
  EditableTextareaComponent,
} from "components/index";
import {
  DocumentDigitalCompatibility,
  FileLabel,
  FileLog,
  FileLogStatus,
  SecondaryLabel,
  SignStatus,
} from "shared/types/file";
import SearchComponent, {
  SearchMode,
} from "../searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession, useUser } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import { UploadProgressPanel } from "shared/components/upload";
import { useUploadQueue } from "shared/hooks/useUploadQueue";
import type { UploadTaskInput } from "shared/hooks/uploadQueue.types";
import {
  MAX_IMAGE_SOURCE_SIZE,
  MAX_UPLOAD_SIZE,
  isClientCompressibleImage,
  isFileWithinUploadLimits,
} from "shared/utils/uploadLimits";
import { Checkmark, Link } from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";
import { toast } from "react-toastify";
import {
  AirTightness,
  CoolingAndSummerComfort,
  CoolingAndSummerComfortInstallation,
  EPCLabel,
  HeatingSystem,
  LedLighting,
  SanitaryHotWater,
  SolarBoiler,
  SolarPanels,
  VentilationSystemType,
  WindowAdvice,
} from "shared/types/epc";
import FileLabelSearchComponent, {
  FileLabelDocumentType,
  FileLabelResourceType,
} from "../searchBar/components/fileLabelSearchComponent";
import TagSelector from "screens/contacts/components/tagSelector";
import {
  Buildings,
  Eye,
  FileText,
  Info,
  MagnifyingGlass,
  StackSimple,
  TagSimple,
  Trash,
} from "@phosphor-icons/react";
import {
  GuaranteeType,
  RentalContract,
  ValidGuaranteeType,
} from "shared/types/rental-contracts";
import { PhotoCard } from "../multiSelectPhotos/photoCard";
import { useResources } from "shared/contexts/resources.context";
import PdfPreview from "./pdfPreview";
import {
  AttributeSelectSection,
  SelectableBuildingAttribute,
} from "./components/AttributeSelectSection";

export enum FileUploadField {
  BUILDING = "Gebouw",
  UNIT = "Unit",
  LABEL = "Label",
  DOCUMENT_DATE = "Documentdatum",
  EXPIRATION_DATE = "Vervaldatum",
  STATUS = "Status",
  REFERENCE_CODE = "Referentie",
  COUNTERPARTIES = "Tegenpartijen",
  SIGN_STATUS = "Ondertekeningsstatus",
  SUMMARY = "Samenvatting",
  DOCUMENT_DIGITAL_COMPATIBILITY = "Document digitaal compatibel",
}

export interface FileUploadSuccess {
  fileLog: FileLog;
  customData: any;
  analyzedFields: any;
}

interface ModalUploadFileLogProps {
  isOpen: boolean;
  onClose?: (fileLog?: FileLog) => void;
  onSave?: (fileLog: any) => void;
  skipFields?: FileUploadField[];
  buildings?: string[];
  building?: any;
  unit?: any;
  customLabels?: FileLabel[] | any;
  defaultFileLabel?: FileLabel;
  defaultSecondaryLabel?: SecondaryLabel;
  droppedFile?: any;
  onFileUploadSuccess?: (uploadSuccessResult: FileUploadSuccess) => void;
  incomingCustomData?: any;
  onOpenChange?: any;
  uploadType?: UploadType;
  selectedFirstCounterparty?: { id: number | null; name: string };
  variant?: "light" | "extended";
  isDismissable?: boolean;
}

export const getStatusColor = (status: FileLogStatus) => {
  if (status == FileLogStatus.OK || status == FileLogStatus.NOT_REQUIRED) {
    return "bg-green-500";
  } else if (status == FileLogStatus.OK_WITH_REMARKS) {
    return "bg-orange-500";
  } else if (status == FileLogStatus.NOT_OK) {
    return "bg-red-500";
  } else {
    return "bg-transparent-500";
  }
};

interface DatabaseFileLabel {
  id: number;
  label: string;
  resourceType: "building" | "unit" | "attribute" | null;
  documentType: "document" | "picture" | null;
  folders: any[];
}

export enum UploadType {
  FILES,
  PICTURES,
}

const ModalUploadFileLog = ({
  isOpen,
  onClose,
  onSave,
  skipFields,
  buildings,
  building,
  unit,
  customLabels,
  defaultFileLabel,
  defaultSecondaryLabel,
  droppedFile,
  onFileUploadSuccess,
  incomingCustomData,
  selectedFirstCounterparty,
  onOpenChange, // used in navigation menu
  uploadType = UploadType.FILES,
  variant = "extended",
  isDismissable = false,
}: ModalUploadFileLogProps) => {
  const { session } = useSession();

  const [fileLogs, setFileLogs] = useState<any[]>([]);
  const [customData, setCustomData] = useState<any>(incomingCustomData || {});
  const { user, isLoaded } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  /* EPC values */
  const [epcLabel, setEpcLabel] = useState("");
  const [epcKwh, setEpcKwh] = useState("");
  const [uValueWalls, setUValueWalls] = useState("");
  const [uValueGlazing, setUValueGlazing] = useState("");
  const [uValueWindows, setUValueWindows] = useState("");
  /* END EPC values */
  const [permitSummary, setPermitSummary] = useState("");
  const [files, setFiles] = useState<any[]>([]);
  const [extraWallInsulationSqm, setExtraWallInsulationSqm] = useState("");
  const [extraWindowAdvice, setExtraWindowAdvice] = useState(
    WindowAdvice.UNKNOWN,
  );
  const [uValueRoofs, setUValueRoofs] = useState("");
  const [extraRoofInsulationSqm, setExtraRoofInsulationSqm] = useState("");
  const [uValueFloors, setUValueFloors] = useState("");
  const [extraFloorInsulationSqm, setExtraFloorInsulationSqm] = useState("");
  const [airTightness, setAirTightness] = useState(AirTightness.UNKNOWN);
  const [coolingAndSummerComfort, setCoolingAndSummerComfort] = useState(
    CoolingAndSummerComfort.UNKNOWN,
  );
  const [solarBoiler, setSolarBoiler] = useState(SolarBoiler.UNKNOWN);
  const [coEmission, setCoEmission] = useState("");
  const [ledLighting, setLedLighting] = useState(LedLighting.UNKNOWN);
  const [sanitaryHotWater, setSanitaryHotWater] = useState(
    SanitaryHotWater.UNKNOWN,
  );
  const [heatingSystem, setHeatingSystem] = useState("");
  const [solarPanels, setSolarPanels] = useState("");
  const [sScore, setSScore] = useState("");
  const [relations, setRelations] = useState<any[]>([]);
  const [buildingId, setBuildingId] = useState<string | null>(null);
  const [rentalGuaranteeAmount, setRentalGuaranteeAmount] =
    useState<string>("");
  const [rentalGuaranteeType, setRentalGuaranteeType] = useState<GuaranteeType>(
    GuaranteeType.TO_CHECK,
  );
  const [modalSize, setModalSize] = useState("3xl");
  const [selectedBuilding, setSelectedBuilding] = useState<any>();
  const [selectedUnit, setSelectedUnit] = useState<any>();
  // Gebouwattribuut (garage/parking/berging) waaraan de upload gekoppeld wordt.
  // Kan voorgeselecteerd zijn via incomingCustomData.buildingAttributeId
  // (bv. vanuit een attribuut-only huurcontract).
  const [selectedAttribute, setSelectedAttribute] =
    useState<SelectableBuildingAttribute | null>(
      incomingCustomData?.buildingAttributeId
        ? { id: incomingCustomData.buildingAttributeId, name: "" }
        : null,
    );

  const [unitId, setUnitId] = useState<string | null>(null);
  const [fileLabel, setFileLabel] = useState<any | null>(
    (defaultFileLabel as any) || null,
  );
  const [secondaryLabels, setSecondaryLabels] = useState<SecondaryLabel[]>([]);

  const [labelSearchOptions, setLabelSearchOptions] = useState<any>(null);
  const [labelSelectOptions, setLabelSelectOptions] = useState<any>();
  const [resourceTypes, setResourceTypes] = useState<
    FileLabelResourceType[] | ["building", "unit"]
  >();
  const [documentTypes, setDocumentTypes] = useState<
    FileLabelDocumentType[] | ["document", "picture"]
  >();
  const [selectedFiles, setSelectedFiles] = useState<FileLog[]>([]);
  const { resources } = useResources();
  const [selectAll, setSelectAll] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);

  // Regex to extract building ID from unit ID
  const buildingIdRegex = /^(\d+-[A-Z]+(?:-[A-Z]+)*-\d+)(?:-[A-Z0-9]+)*$/;

  const [
    coolingAndSummerComfortInstallation,
    setCoolingAndSummerComfortInstallation,
  ] = useState(CoolingAndSummerComfortInstallation.UNKNOWN);
  const [ventilationSystemType, setVentilationSystemType] = useState(
    VentilationSystemType.UNKNOWN,
  );
  const [isInit, setIsInit] = useState<boolean>(false);
  const [databaseFileLabels, setDatabaseFileLabels] = useState<
    DatabaseFileLabel[]
  >([]);

  /**
   * Shared upload engine: bounded concurrency, client-side image compression and
   * live per-file progress. Replaces the sequential request-per-file loop this
   * modal used to run.
   */
  const uploadQueue = useUploadQueue({
    session,
    url: `${API_URL}/file/log`,
    onTaskSuccess: (task) => {
      const savedFileLog: any = task.response;
      if (!savedFileLog) return;
      onSave && onSave(savedFileLog);
      onFileUploadSuccess && onFileUploadSuccess(savedFileLog);
      window.dispatchEvent(new CustomEvent("refreshDocuments"));
    },
  });

  useEffect(() => {
    const fetchFileLabels = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/search-labels?term=&resourceTypes=building,unit,attribute&documentTypes=${
            uploadType === UploadType.PICTURES ? "picture" : "document"
          }`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
          session,
        );
        if (!response.ok) throw new Error("Failed to fetch labels");

        const labels: DatabaseFileLabel[] = await response.json();
        // sort alfabetically
        labels.sort((a, b) => a.label.localeCompare(b.label));
        setDatabaseFileLabels(labels);
      } catch (error) {
        console.error("Error fetching labels:", error);
        setDatabaseFileLabels([]);
      }
    };

    fetchFileLabels();
  }, [uploadType]);

  const handleFileChange = (selectedFiles: any) => {
    const newFileLogs = [...fileLogs];
    const newFiles = [...files, ...selectedFiles];

    selectedFiles.forEach((file: any) => {
      newFileLogs.push({
        file,
        fileLog: {
          documentDigitalCompatibility: null,
          status: FileLogStatus.UNKNOWN,
          statusDescription: "",
          signStatus: SignStatus.UNKNOWN,
          summary: "",
          documentDate: null,
          expirationDate: null,
          resourceTypes:
            unitId || selectedUnit?.id
              ? ["unit"]
              : selectedAttribute
                ? ["attribute", "building"]
                : ["building"],
          documentTypes:
            uploadType == UploadType.PICTURES ? ["picture"] : ["document"],
          fileLabel: {
            label: fileLabel || null,
            resourceType: null,
            documentType: null,
          },
          unitId: null,
        },
        viewInlinePdf: false,
      });
    });

    setFiles(newFiles);
    setFileLogs(newFileLogs);
  };

  useEffect(() => {
    if (customLabels) {
      setLabelSearchOptions(customLabels);
    }
  }, [customLabels]);

  useEffect(() => {
    // Determine resource type based on unit/attribute/building hierarchy.
    // With an attribute selected we keep building labels available too, so
    // contract-related documents (e.g. Huurcontract) remain selectable.
    if (unitId || selectedUnit?.id || fileLogs[0]?.fileLog.unitId) {
      setResourceTypes(["unit"]);
    } else if (selectedAttribute) {
      setResourceTypes(["attribute", "building"]);
    } else if (
      buildingId ||
      selectedBuilding?.id ||
      fileLogs[0]?.fileLog.buildingId
    ) {
      setResourceTypes(["building"]);
    } else {
      setResourceTypes(["building", "unit"]);
    }
  }, [unitId, selectedUnit, selectedAttribute, buildingId, selectedBuilding, fileLogs]);

  useEffect(() => {
    // Set document types based on upload type
    switch (uploadType) {
      case UploadType.PICTURES:
        setDocumentTypes(["picture"]);
        break;
      case UploadType.FILES:
        setDocumentTypes(["document"]);
        break;
      default:
        setDocumentTypes(["document", "picture"]);
    }
  }, [uploadType]);

  useEffect(() => {
    if (files && files.length > 1) {
      setModalSize("full");
    }

    if ((files && files.length == 1) || files.length == 0) {
      setModalSize("5xl");
    }
  }, [files]);

  useEffect(() => {
    setSelectedBuilding(building);
    setBuildingId(buildingId || building?.id || null);
  }, [building]);

  (useEffect(() => {
    setSelectedUnit(unit);
    setUnitId(unitId || unit?.id || null);
  }),
    [unit]);

  useEffect(() => {
    if (customLabels) {
      // If custom labels exist, simply map them as value-label pairs.
      setLabelSelectOptions(
        customLabels.map((value: any) => ({
          value: value,
          label: value,
        })),
      );
      return;
    }

    // Determine allowed resource types based on current selections in the modal.
    // An attribute selection also keeps building labels available (contract docs).
    let allowedResourceTypes: ("unit" | "building" | "attribute")[] | null = null;
    if (
      unitId ||
      (selectedUnit && selectedUnit.id) ||
      (fileLogs.length && fileLogs[0].fileLog.unitId)
    ) {
      allowedResourceTypes = ["unit"];
    } else if (selectedAttribute) {
      allowedResourceTypes = ["attribute", "building"];
    } else if (
      buildingId ||
      (selectedBuilding && selectedBuilding.id) ||
      (fileLogs.length && fileLogs[0].fileLog.buildingId)
    ) {
      allowedResourceTypes = ["building"];
    }

    const docType = uploadType === UploadType.PICTURES ? "picture" : "document";

    // Filter labels based on whether they have folders, resourceTypes (if defined) and document type.
    // Attribute labels don't use the folder architecture (routing happens via the
    // attribute's own SharePoint folder), so the folders requirement is skipped for them.
    const filteredLabels = databaseFileLabels
      .filter(
        (label) =>
          (label.resourceType === "attribute" || label.folders?.length > 0) &&
          (!allowedResourceTypes ||
            allowedResourceTypes.includes(label.resourceType as any)) &&
          label.documentType === docType,
      )
      .map((label) => ({
        value: label.label,
        label: label.label,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)); // Ensure alphabetical sorting

    setLabelSelectOptions(filteredLabels);
  }, [
    customLabels,
    selectedUnit,
    selectedBuilding,
    selectedAttribute,
    fileLogs,
    uploadType,
    buildingId,
    unitId,
    databaseFileLabels, // dependency added to update when database labels change
  ]);

  useEffect(() => {
    console.log(labelSearchOptions);
  }, [labelSearchOptions]);

  useEffect(() => {
    console.log(labelSelectOptions);
  }, [labelSelectOptions]);

  useEffect(() => {
    // Ensure user data is fully loaded before setting up file log state
    if (isLoaded && user && user.id) {
      console.log("User is loaded, and we have a userId:", user.id);
      setIsInit(true);
    } else {
      // Handle unauthenticated or loading state
      setIsInit(false);
    }
  }, [user, isLoaded]);

  useEffect(() => {
    setCustomData(incomingCustomData || {});
    setSelectedAttribute(
      incomingCustomData?.buildingAttributeId
        ? { id: incomingCustomData.buildingAttributeId, name: "" }
        : null,
    );
  }, [incomingCustomData]);

  useEffect(() => {
    setRelations(
      selectedFirstCounterparty ? [selectedFirstCounterparty as any] : [],
    );
  }, [selectedFirstCounterparty]);

  useEffect(() => {
    setFileLabel(defaultFileLabel as FileLabel);
  }, [defaultFileLabel]);

  (useEffect(() => {
    if (defaultSecondaryLabel)
      setSecondaryLabels([defaultSecondaryLabel as SecondaryLabel]);
  }),
    [defaultSecondaryLabel]);

  const handleResetComponent = () => {
    setFileLogs([]);
    setCustomData(incomingCustomData || {});
    setSelectedAttribute(
      incomingCustomData?.buildingAttributeId
        ? { id: incomingCustomData.buildingAttributeId, name: "" }
        : null,
    );
    setEpcLabel("");
    setEpcKwh("");
    setUValueWalls("");
    setUValueGlazing("");
    setUValueWindows("");
    setPermitSummary("");
    setRentalGuaranteeAmount("");
    setRentalGuaranteeType(GuaranteeType.TO_CHECK);
    setFileLabel(null);
    setRelations([]);
    setFiles([]);
    setLabelSearchOptions(null);
    setResourceTypes(["building", "unit"]);
    setDocumentTypes(["document", "picture"]);
    setLabelSelectOptions(null);
  };

  useEffect(() => {
    if (isOpen) {
      // Initialize when opening
      setBuildingId(building?.id || null);
      setUnitId(unit?.id || null);
      setSelectedBuilding(building || null);
      setSelectedUnit(unit || null);
    } else {
      // Reset when closing
      setBuildingId(null);
      setUnitId(null);
      setSelectedBuilding(null);
      setSelectedUnit(null);
    }
  }, [isOpen, building, unit]);

  const handleClose = () => {
    // Reset state here as well, similar to the useEffect above
    handleResetComponent();
    if (onClose) onClose();
  };

  useEffect(() => {
    if (droppedFile) {
      handleFileChange(droppedFile);
    }
  }, [droppedFile]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    field: keyof any,
  ) => {
    const newFileLogs = [...fileLogs];
    newFileLogs[index].fileLog[field] = e.target.value;
    setFileLogs(newFileLogs);
  };

  const handleViewInlinePdf = (fileLog: any, index: number) => {
    const newViewInlinePdfState = !fileLog.viewInlinePdf;
    const newFileLogs = [...fileLogs];
    newFileLogs[index].viewInlinePdf = newViewInlinePdfState;
    setFileLogs(newFileLogs);
  };

  const getLabelForFileLogStatus = (value: FileLogStatus) => {
    switch (value) {
      case FileLogStatus.OK:
        return "Conform";
      case FileLogStatus.NOT_REQUIRED:
        return "Niet van toepassing";
      case FileLogStatus.OK_WITH_REMARKS:
        return "Conform, met opmerkingen";
      case FileLogStatus.NOT_OK:
        return "Niet conform";
      case FileLogStatus.UNKNOWN:
        return "Onbekend";
    }
  };

  const isEpcCommon = () => {
    return fileLabel === FileLabel.EPCCommon;
  };

  const handleLabelSelectChange = (label: any, index: any) => {
    const updatedFileLogs = [...fileLogs];
    updatedFileLogs[index] = {
      ...updatedFileLogs[index],
      fileLog: {
        ...updatedFileLogs[index].fileLog,
        fileLabel: label,
      },
    };

    const hasEpcLabel = isEpcLabelSelected(label);

    if (hasEpcLabel) {
      const filteredFileLogs = updatedFileLogs.filter(
        (fileLog) => !isEpcLabelSelected(fileLog.fileLog.fileLabel.label),
      );
      const filteredFiles = files.filter(
        (file, fileIndex) => file && fileIndex !== index,
      );
      toast.warning(
        "EPCs kunnen alleen één per één worden opgeladen omdat je nog gedetailleerde waarden moet ingeven. Alle EPC's wordt uit de lijst van bestanden verwijderd. Je kan wel met de andere bestanden verdergaan.",
      );
      setFiles(filteredFiles);
      setFileLogs(filteredFileLogs);
    } else {
      setFileLogs(updatedFileLogs);
    }
  };

  const handleSelectChange = (
    value: string,
    index: number,
    field: keyof any,
  ) => {
    const newFileLogs = [...fileLogs];
    newFileLogs[index].fileLog[field] = value;
    setFileLogs(newFileLogs);
  };

  const handleSummaryChange = (value: any, index: number, field: keyof any) => {
    const newFileLogs = [...fileLogs];
    newFileLogs[index].fileLog[field] = value;
    setFileLogs(newFileLogs);
  };

  const isEpcLabelSelected = (incomingLabel?: any) => {
    const label = incomingLabel || fileLabel;
    return label === FileLabel.EPCUnit;
  };

  const isElevatorInfoLabelSelected = () => {
    return (
      fileLabel === FileLabel.ElevatorInspectionCertificate ||
      fileLabel === FileLabel.ElevatorMaintenanceContract ||
      fileLabel === FileLabel.ElevatorRiskAnalysis
    );
  };

  const isUrbanExtractLabelSelected = () => {
    return fileLabel === FileLabel.StedenbouwkundigeInlichtingen;
  };

  const isExpirableDocumentSelect = () => {
    return (
      fileLabel === FileLabel.CollectieveVerwarmingKeuringsattest ||
      fileLabel === FileLabel.CollectieveVerwarmingOnderhoudsattest ||
      fileLabel === FileLabel.EPCUnit ||
      fileLabel === FileLabel.EPCCommon ||
      fileLabel === FileLabel.VerwarmingOnderhoudsattest ||
      fileLabel === FileLabel.VerwarmingKeuringsattest
    );
  };

  const isPermitLabelSelected = () => {
    return (
      fileLabel === FileLabel.Omgevingsvergunning ||
      fileLabel === FileLabel.Milieuvergunning
    );
  };

  const isDangerousMaterialDocumentLabelSelected = () => {
    return (
      fileLabel === FileLabel.Asbestattest ||
      fileLabel === FileLabel.Asbestinventaris ||
      fileLabel === FileLabel.AsbestosRemovalInvoice
    );
  };

  const isBuildingStabilityDocumentLabelSelected = () => {
    return (
      fileLabel === FileLabel.StabilityCommunication ||
      fileLabel === FileLabel.StabilityReportVisit ||
      fileLabel === FileLabel.StabilityStudy
    );
  };

  const isBuildingPlanLabelSelected = () => {
    return (
      fileLabel === FileLabel.PlanCommercieel ||
      fileLabel === FileLabel.PlanGeneriek ||
      fileLabel === FileLabel.PlanLandmeter ||
      fileLabel === FileLabel.PlanUitvoering ||
      fileLabel === FileLabel.PlanVergunning ||
      fileLabel === FileLabel.PlanVoorontwerp ||
      fileLabel === FileLabel.PlanVoorstelNieuweToestand ||
      fileLabel === "Plan riolering"
    );
  };

  const isBuildingOwnershipDocumentSelected = (label: FileLabel) => {
    return (
      label === FileLabel.AttestAankoop ||
      label === FileLabel.Basisakte ||
      label === FileLabel.Biedbrief ||
      label === FileLabel.Eigendomsakte ||
      label === FileLabel.LOI ||
      label === FileLabel.OnderhandseOvereenkomst ||
      label === FileLabel.ReglementMedeEigendom ||
      label === FileLabel.SPA
    );
  };

  const isTaxationDocumentSelected = () => {
    return (
      fileLabel === FileLabel.TaxatieBerekeningen ||
      fileLabel === FileLabel.SchattingTaxatieverslag
    );
  };

  const isRentalGuarantee = () => {
    return (fileLabel?.label || fileLabel) === FileLabel.Huurwaarborg;
  };

  const isRentalContract = () => {
    return customData && customData.rentalContractId;
  };

  const isRentalRelatedDocument = (label: FileLabel) => {
    return (
      label === FileLabel.Huurcontract ||
      label === FileLabel.AnnexHuurcontract ||
      label === FileLabel.AddendumHuurcontract ||
      label === FileLabel.GarageAddendumContract ||
      label === FileLabel.Huurhernieuwingsaanvraag ||
      label === FileLabel.Huurwaarborg ||
      label === FileLabel.IngaandePlaatsbeschrijving ||
      label === FileLabel.UitgaandePlaatsbeschrijving ||
      label === FileLabel.IdentiteitskaartHuurder ||
      label === FileLabel.BetalingsbewijsHuur ||
      label === FileLabel.Indexatiebrief ||
      label === FileLabel.BrandverzekeringHuurder ||
      label === FileLabel.CommunicatieHuurder ||
      label === FileLabel.RegistratiebewijsHuurcontract ||
      label === FileLabel.Opzegbrief ||
      label === FileLabel.RegistratiebewijsPlaatsbeschrijving ||
      label === FileLabel.ReglementInwendigeOrde ||
      label === FileLabel.JusticeOfPeaceCommunication ||
      label === FileLabel.JusticeOfPeaceDeed ||
      label === FileLabel.JusticeOfPeaceRequest ||
      label === FileLabel.JusticeOfPeaceSummons ||
      label === FileLabel.BundleJusticeOfPeace ||
      label === FileLabel.BundleLawyerCounterparty ||
      label === FileLabel.CommunicationLawyerOwn ||
      label === FileLabel.CommunicationLawyerCounterparty
    );
  };

  const isCompanyDocument = () => {
    return customData && customData.yallyCompanyId;
  };

  const handleFileUpload = async () => {
    /* Log the full fields so I can check where data might be missing */
    console.log("File logs:");
    console.log(fileLogs);
    console.log("Custom data:");
    console.log(customData);

    console.log(user);
    if (!user || !user.id) {
      return toast.error(
        "Je gebruikersinformatie is nog niet ingeladen, gelieve even te wachten vooraleer op te slaan.",
      );
    }

    if (!files || (files && files.length == 0)) {
      return toast.error("Je moet een bestand toevoegen");
    }

    if (!customData.yallyCompanyId && !buildingId && !unitId) {
      return toast.error("Je moet een gebouw of unit selecteren");
    }

    if (uploadQueue.summary.isRunning) {
      return toast.info("Er loopt al een upload. Wacht tot deze klaar is.");
    }

    setIsLoading(true); // Start loading

    // This loop only validates and assembles payloads now - no network calls. The
    // uploads themselves run afterwards through the shared queue, which is what
    // turns a strictly sequential "one full round trip per file" batch into a few
    // concurrent workers with progress and a time estimate.
    const uploadTasks: UploadTaskInput[] = [];

    for (let i = 0; i < fileLogs.length; i++) {
      const { file, fileLog } = fileLogs[i];

      if (!isFileWithinUploadLimits(file)) {
        setIsLoading(false);
        const limitMb = isClientCompressibleImage(file)
          ? MAX_IMAGE_SOURCE_SIZE / 1024 / 1024
          : MAX_UPLOAD_SIZE / 1024 / 1024;
        return toast.error(
          `${file.name} is te groot (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum is ${limitMb}MB`,
        );
      }

      if (!fileLog.fileLabel.label && !fileLabel) {
        setIsLoading(false);
        return toast.error(
          "Je moet een bestandslabel toevoegen. Anders kan dit niet op SharePoint worden opgeslagen",
        );
      }

      if (selectedFirstCounterparty && !selectedFirstCounterparty.id) {
        setIsLoading(false);
        return toast.error(
          "Er is iets misgegaan bij het selecteren van de tegenpartij",
        );
      }

      if (isDangerousMaterialDocumentLabelSelected()) {
        // buildingInspectionId is set in incomingCustomData
        customData.dangerousMaterialDocuments = {};
      }

      if (isBuildingPlanLabelSelected()) {
        if (!fileLog?.documentDigitalCompatibility) {
          setIsLoading(false);
          return toast.error(
            "Je moet aangeven of het document ingescand is of een originele pdf! Dit is belangrijk om te weten of we dit kunnen doorsturen naar architecten, etc.",
          );
        }
        // planInfoId is set in incomingCustomData
        customData.buildingPlans = {};
      }

      console.log("Custom data:");
      console.log(customData);

      if (isRentalContract() || isCompanyDocument() || isEpcCommon()) {
        // rentalContractId is set in incomingCustomData
        if (!fileLog.documentDate) {
          setIsLoading(false);
          return toast.error(
            "Bij dit type document is er altijd een documentdatum!",
          );
        }
      }

      if (isRentalGuarantee()) {
        if (!rentalGuaranteeAmount || rentalGuaranteeAmount == "") {
          setIsLoading(false);
          return toast.error(
            "Gelieve het bedrag van de huurwaarborg in te vullen",
          );
        }
        if (
          !rentalGuaranteeAmount ||
          rentalGuaranteeType == GuaranteeType.TO_CHECK
        ) {
          setIsLoading(false);
          return toast.error(
            "Gelieve het type van de huurwaarborg in te vullen",
          );
        }

        console.log("Custom data:");
        console.log(customData);

        if (!customData.rentalContractId && !customData.rentalGuaranteeId) {
          setIsLoading(false);
          return toast.error(
            "Er is geen aan de huurgarantie te koppelen id aanwezig, gelieve Bjorn te contacteren",
          );
        }

        if (!relations || relations.length == 0) {
          setIsLoading(false);
          return toast.error(
            "Gelieve de tegenpartij toe te voegen waar de waarborg is gevestigd",
          );
        }

        // rentalGuaranteeId is set in incomingCustomData
        customData.rentalGuarantee = {
          amount: rentalGuaranteeAmount,
          type: rentalGuaranteeType,
          contractId: (customData && customData.rentalContractId) || null,
          guaranteeId: (customData && customData.rentalGuaranteeId) || null,
        };
      }

      if (isRentalRelatedDocument(fileLog.label)) {
        if (!fileLog.documentDate) {
          setIsLoading(false);
          return toast.error(
            "Bij een document gerelateerd aan de huur is er altijd een documentdatum!",
          );
        }

        if (!customData.rentalContractId) {
          setIsLoading(false);
          return toast.error(
            "Er is geen huurcontract id aanwezig, gelieve Bjorn te contacteren",
          );
        }
      }

      if (
        ((fileLog.label && fileLog.label.label) || fileLog.label) ==
          FileLabel.Huurwaarborg &&
        !fileLog.documentDate
      ) {
        setIsLoading(false);
        return toast.error(
          "Bij een document gerelateerd aan de huurwaarborg is er altijd een documentdatum!",
        );
      }

      if (
        ((fileLog.label && fileLog.label.label) || fileLog.label) ==
          FileLabel.Huurwaarborg &&
        !fileLog.referenceCode
      ) {
        setIsLoading(false);
        return toast.error(
          "Bij een document gerelateerd aan de huurwaarborg is er altijd een referentiecode!",
        );
      }

      if (
        ((fileLog.label && fileLog.label.label) || fileLog.label) ==
          FileLabel.Huurwaarborg &&
        !fileLog.signStatus
      ) {
        setIsLoading(false);
        return toast.error(
          "Bij een document gerelateerd aan de huurwaarborg is er altijd een ondertekeningsstatus!",
        );
      }

      if (
        ((fileLog.label && fileLog.label.label) || fileLog.label) ==
          FileLabel.Huurwaarborg &&
        !customData.rentalGuaranteeId
      ) {
        setIsLoading(false);
        return toast.error(
          "Er is geen huurwaarborg id aanwezig, gelieve Bjorn te contacteren",
        );
      }

      if (
        isRentalRelatedDocument(
          (fileLog.label && fileLog.label.label) || fileLog.label,
        )
      ) {
        if (!fileLog.documentDate) {
          setIsLoading(false);
          return toast.error(
            "Bij een document gerelateerd aan de huur is er altijd een documentdatum!",
          );
        }

        if (!customData.rentalContractId) {
          setIsLoading(false);
          return toast.error(
            "Er is geen huurcontract id aanwezig, gelieve Bjorn te contacteren",
          );
        }
      }

      if (
        isExpirableDocumentSelect() &&
        (!fileLog.expirationDate || !fileLog.documentDate)
      ) {
        setIsLoading(false);
        return toast.error(
          "Bij dit type document is er altijd een document- en vervaldatum!",
        );
      }

      if (isTaxationDocumentSelected()) {
        // stabilityInfoId is set in incomingCustomData
        customData.taxationInfo = {};
      }

      if (isBuildingStabilityDocumentLabelSelected()) {
        // stabilityInfoId is set in incomingCustomData
        customData.stabilityInfo = {};
      }

      if (isBuildingOwnershipDocumentSelected(fileLog.label)) {
        customData.ownershipInfo = {};
      }

      if (
        isEpcLabelSelected() &&
        (selectedUnit || unitId) &&
        (!epcLabel || !epcKwh)
      ) {
        setIsLoading(false);
        return toast.error(
          "EPC label en kWh waarde moeten altijd ingevuld zijn voor een EPC horende bij een unit",
        );
      }

      // if (isEpcLabelSelected() && (unit && (!uValueGlazing || !uValueWalls || !uValueWindows))) {
      //   return toast.warning("De u-waarden voor beglazing, muren en ramen moeten altijd ingevuld zijn");
      // }

      if (isEpcLabelSelected()) {
        function replaceCommaAndParseFloat(value: any) {
          return parseFloat(value.replace(",", "."));
        }

        // Validating EPC kWh (can be a float or integer)
        if (epcKwh) {
          const parsedEpcKwh = replaceCommaAndParseFloat(epcKwh);
          if (isNaN(parsedEpcKwh)) {
            setIsLoading(false);
            return toast.error("EPC kWh moet een getal zijn");
          }
        }

        // Validating U-value Walls (can be a float or integer)
        if (uValueWalls) {
          const parsedUValueWalls = replaceCommaAndParseFloat(uValueWalls);
          if (isNaN(parsedUValueWalls)) {
            setIsLoading(false);
            return toast.error("U-waarde muren moet een getal zijn");
          }
        }

        // Assuming U-value Glazing, U-value Windows, U-value Roofs, U-value Floors, CO Emission, S-Score, and extra insulation fields can also be floats or integers

        // U-value Glazing
        if (uValueGlazing) {
          const parsedUValueGlazing = replaceCommaAndParseFloat(uValueGlazing);
          if (isNaN(parsedUValueGlazing)) {
            setIsLoading(false);
            return toast.error("U-waarde beglazing moet een getal zijn");
          }
        }

        // U-value Windows
        if (uValueWindows) {
          const parsedUValueWindows = replaceCommaAndParseFloat(uValueWindows);
          if (isNaN(parsedUValueWindows)) {
            setIsLoading(false);
            return toast.error("U-waarde ramen moet een getal zijn");
          }
        }

        // U-value Roofs
        if (uValueRoofs) {
          const parsedUValueRoofs = replaceCommaAndParseFloat(uValueRoofs);
          if (isNaN(parsedUValueRoofs)) {
            setIsLoading(false);
            return toast.error("U-waarde daken moet een getal zijn");
          }
        }

        // U-value Floors
        if (uValueFloors) {
          const parsedUValueFloors = replaceCommaAndParseFloat(uValueFloors);
          if (isNaN(parsedUValueFloors)) {
            setIsLoading(false);
            return toast.error("U-waarde vloeren moet een getal zijn");
          }
        }

        // CO2 Emission
        if (coEmission) {
          const parsedCoEmission = replaceCommaAndParseFloat(coEmission);
          if (isNaN(parsedCoEmission)) {
            setIsLoading(false);
            return toast.error("CO2 uitstoot moet een getal zijn");
          }
        }

        // S-Score
        if (sScore) {
          const parsedSScore = replaceCommaAndParseFloat(sScore);
          if (!Number.isInteger(parsedSScore)) {
            setIsLoading(false);
            return toast.error("S-Score moet een geheel getal zijn");
          }
        }

        // Extra Wall Insulation SqM
        if (extraWallInsulationSqm) {
          const parsedExtraWallInsulationSqm = replaceCommaAndParseFloat(
            extraWallInsulationSqm,
          );
          if (isNaN(parsedExtraWallInsulationSqm)) {
            setIsLoading(false);
            return toast.error("Extra muurisolatie moet een getal zijn");
          }
        }

        // Extra Roof Insulation SqM
        if (extraRoofInsulationSqm) {
          const parsedExtraRoofInsulationSqm = replaceCommaAndParseFloat(
            extraRoofInsulationSqm,
          );
          if (isNaN(parsedExtraRoofInsulationSqm)) {
            setIsLoading(false);
            return toast.error("Extra dakisolatie moet een getal zijn");
          }
        }

        // Extra Floor Insulation SqM
        if (extraFloorInsulationSqm) {
          const parsedExtraFloorInsulationSqm = replaceCommaAndParseFloat(
            extraFloorInsulationSqm,
          );
          if (isNaN(parsedExtraFloorInsulationSqm)) {
            setIsLoading(false);
            return toast.error("Extra vloerisolatie moet een getal zijn");
          }
        }

        customData.epc = {
          epcLabel: epcLabel,
          kWh: epcKwh,
          uValueGlazing: uValueGlazing,
          uValueWalls: uValueWalls,
          extraWallInsulationSqm: extraWallInsulationSqm,
          uValueWindows: uValueWindows,
          extraWindowAdvice: extraWindowAdvice,
          uValueRoofs: uValueRoofs,
          extraRoofInsulationSqm: extraRoofInsulationSqm,
          uValueFloors: uValueFloors,
          extraFloorInsulationSqm: extraFloorInsulationSqm,
          airTightness: airTightness,
          coolingAndSummerComfort: coolingAndSummerComfort,
          coolingAndSummerComfortInstallation:
            coolingAndSummerComfortInstallation,
          solarBoiler: solarBoiler,
          coEmission: coEmission,
          ledLighting: ledLighting,
          sanitaryHotWater: sanitaryHotWater,
          heatingSystem: heatingSystem,
          solarPanels: solarPanels,
          sScore: sScore,
        };
        if (!fileLog.documentDate || !fileLog.expirationDate) {
          setIsLoading(false);
          return toast.error(
            "Bij een EPC is er altijd een document- en vervaldatum!",
          );
        }

        // if (files.length > 1) {
        //   return toast.error(
        //     "Je kan maar één EPC tegelijk toevoegen want de ingegeven data moet specifiek zijn",
        //   );
        // }
      }

      if (isPermitLabelSelected()) {
        const effectiveSummary = permitSummary || fileLog.summary;
        if (!effectiveSummary || effectiveSummary.trim() === "") {
          setIsLoading(false);
          return toast.error(
            "Gelieve een korte samenvatting in te geven zodat collega's weten over wat deze vergunning gaat!",
          );
        }
        customData.permit = { summary: effectiveSummary };
        if (!fileLog.documentDate) {
          setIsLoading(false);
          return toast.error(
            "Bij een vergunning is er altijd een documentdatum!",
          );
        }

        // if (files.length > 1) {
        //   return toast.error("Je kan maar één vergunning tegelijk toevoegen");
        // }
      }

      if (
        isBuildingOwnershipDocumentSelected(fileLog.label) ||
        isRentalContract()
      ) {
        setIsLoading(false);
        if (!fileLog.signStatus || fileLog.signStatus == SignStatus.UNKNOWN) {
          return toast.error(
            "Geef de ondertekeningsstatus van het document aan!",
          );
        }
      }

      if (isElevatorInfoLabelSelected()) {
        setIsLoading(false);
        if (!fileLog.documentDate) {
          return toast.error(
            "Bij een lift-document is er altijd een documentdatum!",
          );
        }

        // if (files.length > 1) {
        //   return toast.error("Je kan maar één lift-document tegelijk toevoegen");
        // }
      }

      if (isUrbanExtractLabelSelected()) {
        setIsLoading(false);
        if (!fileLog.documentDate) {
          return toast.error(
            "Bij een stedenbouwkundig uittreksel is er altijd een documentdatum!",
          );
        }
        // if (files.length > 1) {
        //   return toast.error(
        //     "Je kan maar één stedenbouwkundig uittreksel tegelijk toevoegen",
        //   );
        // }
      }

      if (!fileLog?.createdByUserId) {
        fileLog.createdByUserId = user.id;
      }

      if (!fileLog?.createdByUserFullName) {
        fileLog.createdByUserFullName = user.fullName || "";
      }

      // Build the request body for this file up front. The payload is captured in
      // a closure so the queue can rebuild it per attempt with the *compressed*
      // file, keeping the metadata consistent with the bytes actually sent.
      const joinedCustomData = {
        ...(customData || {}),
        ...(fileLog.customData || {}),
      };

      // Empty arrays are truthy in JS, so "a || b" returns the empty per-file
      // array instead of the user's modal selection. Normalize to strings
      // and fall back to the modal state when the per-file list is empty.
      const rawSecondaryLabels: any[] =
        Array.isArray(fileLog.secondaryLabels) && fileLog.secondaryLabels.length > 0
          ? fileLog.secondaryLabels
          : secondaryLabels || [];
      const normalizedSecondaryLabels = rawSecondaryLabels
        .map((l: any) => (typeof l === "string" ? l : l?.label))
        .filter(Boolean);

      const fileLogPayload = {
        ...fileLog,
        secondaryLabels: normalizedSecondaryLabels,
        buildingId: buildingId,
        unitId: fileLog.unitId || unitId,
        fileLabel: fileLog.fileLabel.label || fileLabel,
        relations: fileLog.relations || relations,
        createdByUserId: user && user.id,
        createdByUserFullName: user && user.fullName,
        // Processed synchronously so every file reports a real outcome. This used
        // to flip to asynchronous for multi-file batches, which returned instantly
        // but left failures invisible and made progress impossible to report.
        processAsynchronously: false,
      };

      uploadTasks.push({
        id: `${i}-${file.name}-${file.lastModified}`,
        file,
        displayName: file.name,
        meta: { index: i },
        buildBody: (uploadFile: File) => {
          const formData = new FormData();
          formData.append("file", uploadFile);
          formData.append("fileLog", JSON.stringify(fileLogPayload));
          formData.append("customData", JSON.stringify(joinedCustomData));
          return formData;
        },
      });
    }

    if (uploadTasks.length === 0) {
      setIsLoading(false);
      return toast.error("Er zijn geen bestanden om te uploaden");
    }

    // Hand the whole batch to the shared queue: a fixed worker pool uploads a few
    // files at a time with client-side compression and live progress, instead of
    // one strictly sequential request per file.
    try {
      const batch = await uploadQueue.start(uploadTasks);

      const succeededIndexes = new Set(
        batch.succeeded.map((task) => (task.meta as any)?.index),
      );

      if (batch.failed.length === 0 && batch.canceled.length === 0) {
        setFiles([]);
        setFileLogs([]);
        setIsLoading(false);
        uploadQueue.reset();
        toast.success(
          `${batch.succeeded.length} bestand(en) succesvol geüpload!`,
        );
        handleClose();
        return;
      }

      // Something needs attention: keep the modal open with only the unfinished
      // files left, so the user can fix and retry rather than start over.
      const remainingIndexes = fileLogs
        .map((_: any, index: number) => index)
        .filter((index: number) => !succeededIndexes.has(index));

      setFiles(remainingIndexes.map((index: number) => files[index]));
      setFileLogs(remainingIndexes.map((index: number) => fileLogs[index]));
      setIsLoading(false);

      if (batch.canceled.length > 0) {
        toast.info(
          `Upload geannuleerd - ${batch.succeeded.length} bestand(en) waren al geüpload`,
        );
      } else if (batch.succeeded.length > 0) {
        toast.warning(
          `${batch.succeeded.length} bestand(en) geüpload, ${batch.failed.length} mislukt`,
        );
      } else {
        toast.error(`Alle ${batch.failed.length} upload(s) mislukt`);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error uploading files:", error);
      Sentry.captureException(error, {
        extra: {
          context: "uploadFileModal.handleFileUpload",
          fileCount: uploadTasks.length,
          buildingId,
          unitId,
          fileLabel: fileLabel?.label || fileLabel,
        },
      });
      toast.error(
        "Er was een error bij het uploaden van de bestanden. Contacteer Bjorn!",
      );
    }
  };

  const handleRemoveTag = async (tag: any) => {
    setSecondaryLabels((prev: any) =>
      prev.filter((label: any) => label !== tag),
    );
  };

  const handleAddTag = async (tag: any) => {
    setSecondaryLabels((prev: any) => [...(prev || []), tag]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(Array.from(e.dataTransfer.files));
      e.dataTransfer.clearData();
    }
  };

  const handleSave = async () => {
    await handleFileUpload();
  };

  const handleFileLabelChange = (label: any) => {
    if (!label) setFileLabel(defaultFileLabel || null);
    setFileLabel(label);
  };

  const handleClearUnit = (index: number) => {
    const resourceType = "building";
    const docType = uploadType === UploadType.PICTURES ? "picture" : "document";

    const newFileLogs = [...fileLogs];
    newFileLogs[index].fileLog.unitId = null;
    newFileLogs[index].fileLog.resourceTypes = [resourceType];
    newFileLogs[index].fileLog.documentTypes = [docType];

    // Don't set the label to null, instead clear it properly through the component
    newFileLogs[index].fileLog.fileLabel = {
      label: "",
      resourceType: resourceType,
    }; // or whatever default structure you need

    const labelOptions = databaseFileLabels
      .filter(
        (label) =>
          label.folders?.length > 0 &&
          label.resourceType === resourceType &&
          label.documentType === docType,
      )
      .map((label) => label.label)
      .sort((a, b) => a.localeCompare(b));

    newFileLogs[index].labelOptions = labelOptions;
    setFileLogs(newFileLogs);
    setSelectedUnit(null);
  };

  const handleUnitSelectForFileLog = (resource: any, index: number) => {
    const newFileLogs = [...fileLogs];
    newFileLogs[index].fileLog.unitId = resource.resourceId;

    // Determine resourceType based on selection and document type based on uploadType
    const resourceType = resource.type;
    const docType = uploadType === UploadType.PICTURES ? "picture" : "document";

    // Filter and sort labels from the stored state
    const labelOptions = databaseFileLabels
      .filter(
        (label) =>
          label.folders?.length > 0 &&
          label.resourceType === resourceType &&
          label.documentType === docType,
      )
      .map((label) => label.label)
      .sort((a, b) => a.localeCompare(b));

    newFileLogs[index] = {
      ...newFileLogs[index],
      fileLog: {
        ...newFileLogs[index].fileLog,
        resourceTypes: [resourceType],
        documentTypes: [docType],
        fileLabel: {
          label: null,
          resourceType: null,
          documentType: null,
        },
      },
      labelOptions: labelOptions,
    };
    setFileLogs(newFileLogs);
  };

  const handleBulkUnitSelectForFileLog = (resource: any) => {
    const newFileLogs = fileLogs.map((fileLog) => {
      if (selectedFiles.includes(fileLog)) {
        const resourceType = resource.type;
        const docType =
          uploadType === UploadType.PICTURES ? "picture" : "document";
        const labelOptions = databaseFileLabels
          .filter(
            (label) =>
              label.folders?.length > 0 &&
              label.resourceType === resourceType &&
              label.documentType === docType,
          )
          .map((label) => label.label)
          .sort((a, b) => a.localeCompare(b));
        return {
          ...fileLog,
          fileLog: {
            ...fileLog.fileLog,
            unitId: resource.resourceId,
            resourceTypes: [resource.type],
            documentTypes: [docType],
          },
          labelOptions: labelOptions,
        };
      }
      return fileLog;
    });
    setFileLogs(newFileLogs);
    setSelectedUnit(null);
  };

  // Koppelt de upload aan een gebouwattribuut (of maakt de koppeling ongedaan).
  // De backend gebruikt customData.buildingAttributeId om het bestand in de
  // SharePoint-map van het attribuut te plaatsen en het FileLog te linken.
  const handleAttributeSelect = (
    attribute: SelectableBuildingAttribute | null,
  ) => {
    setSelectedAttribute(attribute);
    setCustomData((prev: any) => {
      const next = { ...(prev || {}) };
      if (attribute) {
        next.buildingAttributeId = attribute.id;
      } else {
        delete next.buildingAttributeId;
      }
      return next;
    });
    // Update the label scope of already-added files; keep chosen labels intact
    // because building labels remain valid alongside attribute labels.
    setFileLogs((prev) =>
      prev.map((entry) => ({
        ...entry,
        fileLog: {
          ...entry.fileLog,
          resourceTypes: entry.fileLog.unitId
            ? ["unit"]
            : attribute
              ? ["attribute", "building"]
              : ["building"],
        },
      })),
    );
  };

  const handleUnitSelect = (resource: any) => {
    console.log(resource);
    const match = resource.resourceId.match(buildingIdRegex);
    if (match) {
      const extractedBuildingId = match[1];
      console.log("Extracted building ID:", extractedBuildingId);
      setBuildingId(extractedBuildingId);
    } else {
      console.log("No match found for building ID regex.");
    }

    setUnitId(resource.resourceId);

    // Een unit en een attribuut sluiten elkaar uit: unit-uploads mogen geen
    // buildingAttributeId meesturen.
    if (resource.resourceId && selectedAttribute) {
      handleAttributeSelect(null);
    }

    // Update the label options based on the selected unit

    if (resource.resourceId) {
      if (uploadType == UploadType.PICTURES) {
        setResourceTypes(["unit"]);
        setDocumentTypes(["picture"]);
      } else {
        setResourceTypes(["unit"]);
        setDocumentTypes(["document"]);
      }
    } else {
      if (uploadType == UploadType.PICTURES) {
        setResourceTypes(["building"]);
        setDocumentTypes(["picture"]);
      } else {
        setResourceTypes(["building"]);
        setDocumentTypes(["document"]);
      }
    }

    setFileLabel(null); // Reset the selected file label if necessary
  };

  const handleRelationOneSelect = (resource: any) => {
    setRelations((prev) => [
      ...prev,
      { resourceType: resource.type, resourceId: resource.resourceId },
    ]);
  };

  const handleBulkRelationSelect = (resource: any) => {
    const updatedFileLogs = fileLogs.map((fileLog) => {
      if (selectedFiles.includes(fileLog)) {
        return {
          ...fileLog,
          fileLog: {
            ...fileLog.fileLog,
            relations: [resource],
          },
        };
      }
      return fileLog;
    });
    setFileLogs(updatedFileLogs);
  };

  const handleFileLogRelationOneSelect = (resource: any, index: number) => {
    const newFileLogs = [...fileLogs];
    newFileLogs[index].fileLog.relations = [resource];
    setFileLogs(newFileLogs);
  };

  const handleRelationTwoSelect = (resource: any) => {
    setRelations((prev) => [
      ...prev,
      { resourceType: resource.type, resourceId: resource.resourceId },
    ]);
  };

  const handleBuildingSelect = (resource: any) => {
    const newBuildingId = resource.resourceId;
    setBuildingId(newBuildingId);

    const newFileLogs = fileLogs.map((fileLog) => ({
      ...fileLog,
      fileLog: {
        ...fileLog.fileLog,
        buildingId: newBuildingId,
        unitId: null,
      },
    }));

    if (uploadType === UploadType.PICTURES) {
      setResourceTypes(["building"]);
      setDocumentTypes(["picture"]);
    } else {
      setResourceTypes(["building"]);
      setDocumentTypes(["document"]);
    }
    setFileLogs(newFileLogs);
  };

  const handleStatusDescriptionChange = (value: string, index: number) => {
    const newFileLogs = [...fileLogs];
    newFileLogs[index].fileLog.statusDescription = value;
    setFileLogs(newFileLogs);
  };

  const handleFileSelect = (fileLog: FileLog) => {
    setSelectedFiles((prev) =>
      prev.includes(fileLog)
        ? prev.filter((file) => file !== fileLog)
        : [...prev, fileLog],
    );
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setSelectedFiles(newSelectAll ? [...fileLogs] : []);
  };

  const handleBulkDocumentDateChange = (date: string) => {
    const updatedFileLogs = fileLogs.map((fileLog) => {
      if (selectedFiles.includes(fileLog)) {
        return {
          ...fileLog,
          fileLog: {
            ...fileLog.fileLog,
            documentDate: date,
          },
        };
      }
      return fileLog;
    });
    setFileLogs(updatedFileLogs);
  };

  const handleBulkExpirationDateChange = (date: string) => {
    const updatedFileLogs = fileLogs.map((fileLog) => {
      if (selectedFiles.includes(fileLog)) {
        return {
          ...fileLog,
          fileLog: {
            ...fileLog.fileLog,
            expirationDate: date,
          },
        };
      }
      return fileLog;
    });
    setFileLogs(updatedFileLogs);
  };

  const handleBulkLabelChange = (label: any) => {
    const updatedFileLogs = fileLogs.map((fileLog) => {
      if (selectedFiles.includes(fileLog)) {
        return {
          ...fileLog,
          fileLog: {
            ...fileLog.fileLog,
            fileLabel: label,
          },
        };
      }
      return fileLog;
    });

    const hasEpcLabel = updatedFileLogs.some((fileLog) =>
      isEpcLabelSelected(fileLog.fileLog.fileLabel.label),
    );

    if (hasEpcLabel) {
      const filteredFileLogs = updatedFileLogs.filter(
        (fileLog) => !isEpcLabelSelected(fileLog.fileLog.fileLabel.label),
      );
      const filteredFiles = files.filter(
        (file) =>
          !updatedFileLogs.some(
            (fileLog) =>
              fileLog.file.name === file.name &&
              isEpcLabelSelected(fileLog.fileLog.fileLabel.label),
          ),
      );
      toast.warning(
        "EPCs kunnen alleen één per één worden opgeladen omdat je nog gedetailleerde waarden moet ingeven. Alle EPC's wordt uit de lijst van bestanden verwijderd. Je kan wel met de andere bestanden verdergaan.",
      );
      setFiles(filteredFiles);
      setFileLogs(filteredFileLogs);
    } else {
      setFileLogs(updatedFileLogs);
      setSelectedFiles([]);
    }
  };

  const handleOwnershipDocumentSignStatusSelect = (
    signStatus: SignStatus,
    index: number,
  ) => {
    const newFileLogs = [...fileLogs];
    newFileLogs[index].fileLog.signStatus = signStatus;

    if (
      signStatus == SignStatus.SIGNED ||
      signStatus == SignStatus.NOT_REQUIRED
    ) {
      newFileLogs[index].fileLog.status = FileLogStatus.OK;
    } else if (signStatus == SignStatus.DRAFT) {
      newFileLogs[index].fileLog.status = FileLogStatus.NOT_OK;
    } else {
      newFileLogs[index].fileLog.status = FileLogStatus.UNKNOWN;
    }

    if (signStatus) {
      newFileLogs[index].fileLog.statusDescription =
        "Document heeft ondertekeningsstatus: " + signStatus;
    } else {
      newFileLogs[index].fileLog.statusDescription = "";
    }

    setFileLogs(newFileLogs);
  };

  const handleEpcLabelSelect = (epcLabel: string) => {
    setEpcLabel(epcLabel);

    if (
      epcLabel == EPCLabel.A_PLUS ||
      epcLabel == EPCLabel.A ||
      epcLabel == EPCLabel.B ||
      epcLabel == EPCLabel.C
    ) {
      setFileLogs((prev: any) =>
        prev.map((fileLog: FileLog) => ({
          ...fileLog,
          status: FileLogStatus.OK,
          statusDescription: "EPC heeft epc label " + epcLabel,
        })),
      );
    } else if (
      epcLabel == EPCLabel.D ||
      epcLabel == EPCLabel.E ||
      epcLabel == EPCLabel.F
    ) {
      setFileLogs((prev: any) =>
        prev.map((fileLog: FileLog) => ({
          ...fileLog,
          status: FileLogStatus.NOT_OK,
          statusDescription: "EPC heeft epc label " + epcLabel,
        })),
      );
    } else {
      setFileLogs((prev: any) =>
        prev.map((fileLog: FileLog) => ({
          ...fileLog,
          status: FileLogStatus.UNKNOWN,
          statusDescription: "",
        })),
      );
    }
  };

  const handleWindowAdviceSelect = (value: WindowAdvice) => {
    setExtraWindowAdvice(value);
  };

  const handleAirTightnessSelect = (value: AirTightness) => {
    setAirTightness(value);
  };

  const handleCoolingAndSummerComfortSelect = (
    value: CoolingAndSummerComfort,
  ) => {
    setCoolingAndSummerComfort(value);
  };

  const handleSolarBoilerSelect = (value: SolarBoiler) => {
    setSolarBoiler(value);
  };

  const handleCoolingAndSummerComfortInstallationSelect = (
    value: CoolingAndSummerComfortInstallation,
  ) => {
    setCoolingAndSummerComfortInstallation(value);
  };

  const handleLedLightingSelect = (value: LedLighting) => {
    setLedLighting(value);
  };

  const handleHeatingSystemSelect = (value: HeatingSystem) => {
    setHeatingSystem(value);
  };

  const handleSanitaryHotWaterSelect = (value: SanitaryHotWater) => {
    setSanitaryHotWater(value);
  };

  const handleSolarPanelsSelect = (value: SolarPanels) => {
    setSolarPanels(value);
  };

  const handleVentilationSystemTypeSelect = (value: VentilationSystemType) => {
    setVentilationSystemType(value);
  };

  const handleContractSelect = (rentalContract: RentalContract) => {
    console.log("Selected rental contract:", rentalContract);
    setCustomData((prev: any) => ({
      ...prev,
      rentalContract: rentalContract,
      rentalContractId: rentalContract.id,
      rentalGuaranteeId:
        rentalContract.guarantee &&
        rentalContract.guarantee.length > 0 &&
        rentalContract.guarantee[0].id,
    }));
  };

  const handleContractSelectForFileLog = (
    rentalContract: RentalContract,
    index: number,
  ) => {
    const newFileLogs = [...fileLogs];
    if (!newFileLogs[index].fileLog.customData) {
      newFileLogs[index].fileLog.customData = {};
    }

    newFileLogs[index].fileLog.customData.rentalContractId = rentalContract.id;
    newFileLogs[index].fileLog.customData.rentalGuaranteeId =
      rentalContract.guarantee &&
      rentalContract.guarantee.length > 0 &&
      rentalContract.guarantee[0].id;
    setFileLogs(newFileLogs);
  };

  const shouldSkipField = (field: FileUploadField) => {
    return skipFields && skipFields.includes(field);
  };

  const handleDeleteFile = (index: number) => {
    const newFileLogs = [...fileLogs];
    const newFiles = [...files];
    newFileLogs.splice(index, 1);
    newFiles.splice(index, 1);
    setFileLogs(newFileLogs);
    setFiles(newFiles);
  };

  const renderColumns = () => {
    return uploadType == UploadType.FILES ? (
      <TableHeader className="">
        <TableColumn className="max-w-[50px]! min-w-[50px] border p-2 text-left">
          <Checkbox
            color="success"
            isSelected={selectAll}
            onChange={handleSelectAll}
            classNames={{
              base: "h-6 w-6",
            }}
          />
        </TableColumn>
        <TableColumn className="min-w-[100px] border p-2 text-left">
          Bestand
        </TableColumn>
        <TableColumn className="min-w-[80px] border p-2 text-left">
          Preview
        </TableColumn>
        <TableColumn className="min-w-[200px] border p-2 text-left">
          Unit
        </TableColumn>
        <TableColumn className="min-w-[150px] border p-2 text-left">
          Tegenpartij
        </TableColumn>
        <TableColumn className="min-w-[200px] border p-2 text-left">
          Contract
        </TableColumn>
        <TableColumn className="min-w-[250px] border p-2 text-left">
          Label
        </TableColumn>
        <TableColumn className="min-w-[250px] border p-2 text-left">
          Samenvatting
        </TableColumn>
        <TableColumn className="min-w-[150px] border p-2 text-left">
          Documentdatum
        </TableColumn>
        <TableColumn className="min-w-[150px] border p-2 text-left">
          Vervaldatum
        </TableColumn>
        <TableColumn className="min-w-[150px] border p-2 text-left">
          Status
        </TableColumn>
        <TableColumn className="min-w-[150px] border p-2 text-left">
          Referentie
        </TableColumn>
        <TableColumn className="min-w-[150px] border p-2 text-left">
          Acties
        </TableColumn>
      </TableHeader>
    ) : (
      <TableHeader className="">
        <TableColumn className="max-w-[50px]! min-w-[50px] border p-2 text-left">
          <Checkbox
            color="success"
            isSelected={selectAll}
            onChange={handleSelectAll}
            classNames={{
              base: "h-6 w-6",
            }}
          />
        </TableColumn>
        <TableColumn className="min-w-[100px] border p-2 text-left">
          Bestand
        </TableColumn>
        <TableColumn className="min-w-[80px] border p-2 text-left">
          Preview
        </TableColumn>
        <TableColumn className="min-w-[200px] border p-2 text-left">
          Unit
        </TableColumn>
        <TableColumn className="min-w-[250px] border p-2 text-left">
          Label
        </TableColumn>
        <TableColumn className="min-w-[250px] border p-2 text-left">
          Samenvatting
        </TableColumn>
        <TableColumn className="min-w-[150px] border p-2 text-left">
          Documentdatum
        </TableColumn>
        <TableColumn className="min-w-[150px] border p-2 text-left">
          Status
        </TableColumn>
        <TableColumn className="min-w-[150px] border p-2 text-left">
          Acties
        </TableColumn>
      </TableHeader>
    );
  };

  const renderRows = () => {
    return uploadType == UploadType.FILES ? (
      <TableBody>
        {fileLogs &&
          fileLogs.map((fileLog, index) => (
            <TableRow key={index}>
              <TableCell className="max-w-[50px] border p-2">
                <Checkbox
                  color="success"
                  isSelected={selectedFiles.includes(fileLog)}
                  onChange={() => handleFileSelect(fileLog)}
                  classNames={{
                    base: "h-6 w-6", // Adjust the size as needed
                  }}
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                {fileLog.file.name}
              </TableCell>

              <TableCell className="min-w-[150px]">
                <div key={index} className="file-item flex gap-2">
                  {fileLog.viewInlinePdf && (
                    <div className="inline-pdf-viewer">
                      <PdfPreview fileLog={fileLog} />
                    </div>
                  )}
                  <Tooltip content="Open in modal">
                    <Eye
                      className="cursor-pointer"
                      onClick={() => {
                        setSelectedPdf(fileLog);
                        setIsPdfModalOpen(true);
                      }}
                    />
                  </Tooltip>
                  <Tooltip
                    content={`${fileLog.viewInlinePdf ? "Sluit inline view" : "Open inline view"}`}
                  >
                    <MagnifyingGlass
                      className="cursor-pointer"
                      onClick={() => {
                        handleViewInlinePdf(fileLog, index);
                      }}
                    />
                  </Tooltip>
                </div>
              </TableCell>

              <TableCell className="min-w-[150px] border p-2">
                <SearchComponent
                  editMode={true}
                  label="Selecteer unit"
                  isCleared={() => {
                    handleClearUnit(index);
                  }}
                  onSelect={(resource: any) =>
                    handleUnitSelectForFileLog(resource, index)
                  }
                  resourceFilter={[ResourceType.UNIT]}
                  initialValue={
                    fileLog.fileLog.unitId || (unit && unit.id) || ""
                  }
                  mode={SearchMode.SEARCH_AND_PRESENT}
                  customData={
                    buildingId
                      ? resources.filter(
                          (resource) =>
                            resource.type === ResourceType.UNIT &&
                            resource.resourceId.startsWith(buildingId),
                        )
                      : resources.filter(
                          (resource) => resource.type === ResourceType.UNIT,
                        )
                  } // Show all units if no building is selected
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                <SearchComponent
                  editMode={true}
                  label="Selecteer tegenpartij"
                  onSelect={(resource: any) =>
                    handleFileLogRelationOneSelect(resource, index)
                  }
                  resourceFilter={[ResourceType.COMPANY, ResourceType.CONTACT]}
                  mode={SearchMode.SEARCH_AND_PRESENT}
                  initialValue={fileLog.fileLog.relations?.[0]?.name || ""}
                />
              </TableCell>
              <TableCell className="min-w-[150px]">
                {(isRentalRelatedDocument(
                  fileLog.fileLog.fileLabel.label as FileLabel,
                ) &&
                  fileLog.fileLog &&
                  fileLog.fileLog.customData &&
                  fileLog.fileLog.customData.rentalContractId && (
                    <div className="flex flex-col gap-4">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <FileText weight="duotone" size={18} />
                        <p>Huurcontract</p>
                      </header>
                      <SearchComponent
                        editMode={true}
                        label="Selecteer contract"
                        onSelect={(resource) =>
                          (isRentalRelatedDocument(
                            fileLog.fileLog.fileLabel.label as FileLabel,
                          ) &&
                            handleContractSelectForFileLog(resource, index)) ||
                          console.log("No contract selected")
                        }
                        mode={SearchMode.SEARCH_AND_PRESENT}
                        api={`${API_URL}/search/rental/${fileLog.fileLog.customData.rentalContractId}`}
                      />
                    </div>
                  )) ||
                  (fileLog.fileLog.unitId && (
                    <div className="flex flex-col gap-4">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <FileText weight="duotone" size={18} />
                        <p>Huurcontract</p>
                      </header>
                      <SearchComponent
                        editMode={true}
                        label="Selecteer contract"
                        onSelect={(resource) =>
                          (isRentalRelatedDocument(
                            fileLog.fileLog.fileLabel.label as FileLabel,
                          ) &&
                            handleContractSelectForFileLog(resource, index)) ||
                          console.log("No contract selected")
                        }
                        mode={SearchMode.SEARCH_AND_PRESENT}
                        api={`${API_URL}/search/rental/unit/${fileLog.fileLog.unitId}/contracts`}
                      />
                    </div>
                  ))}
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                <FileLabelSearchComponent
                  key={`${fileLog.fileLog.resourceTypes?.[0]}-${fileLog.fileLog.documentTypes?.[0]}`}
                  incomingSelectedLabel={
                    fileLog.fileLog.fileLabel.label || null
                  }
                  incomingDocumentTypes={fileLog.fileLog.documentTypes || []}
                  incomingResourceTypes={fileLog.fileLog.resourceTypes || []}
                  onSelect={(label: any) =>
                    handleLabelSelectChange(label, index)
                  }
                  labelType="full"
                />
                {fileLog.fileLog.fileLabel.label && (
                  <div className="mt-2">
                    {/* Warning for unit label without unit */}
                    {fileLog.fileLog.fileLabel.resourceType === "unit" &&
                      !fileLog.fileLog.unitId && (
                        <div className="text-warning flex items-center gap-1">
                          <Info size={16} weight="duotone" />
                          <span className="text-sm">
                            Unit label geselecteerd maar geen unit ingevuld
                          </span>
                        </div>
                      )}

                    {/* Warning for building label with unit */}
                    {fileLog.fileLog.fileLabel.resourceType === "building" &&
                      fileLog.fileLog.unitId && (
                        <div className="text-warning flex cursor-help items-center gap-1">
                          <Info size={16} weight="duotone" />
                          <span className="text-sm">
                            Gebouw label geselecteerd maar unit ingevuld
                          </span>
                        </div>
                      )}

                    {/* Warning for attribute label without a selected attribute */}
                    {fileLog.fileLog.fileLabel.resourceType === "attribute" &&
                      !selectedAttribute && (
                        <div className="text-warning flex items-center gap-1">
                          <Info size={16} weight="duotone" />
                          <span className="text-sm">
                            Attribuut label geselecteerd maar geen attribuut
                            gekozen
                          </span>
                        </div>
                      )}
                  </div>
                )}
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                <EditableTextareaComponent
                  label=""
                  value={fileLog.fileLog.summary || ""}
                  editMode={true}
                  isLoading={false}
                  onChange={(value) =>
                    handleSummaryChange(value, index, "summary")
                  }
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                <Input
                  label=""
                  type="date"
                  value={(fileLog.fileLog.documentDate as string) || ""}
                  onChange={(e) => handleInputChange(e, index, "documentDate")}
                  classNames={{
                    input:
                      "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
                    inputWrapper:
                      "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                  }}
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                <Input
                  label=""
                  type="date"
                  value={(fileLog.fileLog.expirationDate as string) || ""}
                  onChange={(e) =>
                    handleInputChange(e, index, "expirationDate")
                  }
                  classNames={{
                    input:
                      "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
                    inputWrapper:
                      "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                  }}
                />
              </TableCell>

              <TableCell className="min-w-[150px] border p-2">
                <EditableSelectComponent
                  label=""
                  editMode={true}
                  value={(fileLog.fileLog.status as string) || ""}
                  options={Object.values(FileLogStatus).map((value) => ({
                    label: value,
                    value,
                  }))}
                  onChange={(value) =>
                    handleSelectChange(value, index, "status")
                  }
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                <Input
                  label=""
                  value={fileLog.fileLog.referenceCode || ""}
                  onChange={(e) => handleInputChange(e, index, "referenceCode")}
                  classNames={{
                    input:
                      "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
                    inputWrapper:
                      "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                  }}
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2 text-center">
                <Tooltip content="Bestand verwijderen">
                  <Trash
                    className="cursor-pointer text-red-500"
                    weight="duotone"
                    size={18}
                    onClick={() => handleDeleteFile(index)}
                  />
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    ) : (
      <TableBody>
        {fileLogs &&
          fileLogs.map((fileLog, index) => (
            <TableRow key={index}>
              <TableCell className="min-w-[150px] border p-2">
                <Checkbox
                  color="success"
                  isSelected={selectedFiles.includes(fileLog)}
                  onChange={() => handleFileSelect(fileLog)}
                  classNames={{
                    base: "h-6 w-6", // Adjust the size as needed
                  }}
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                {fileLog.file.name}
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                <PhotoCard
                  key={fileLog.file.name}
                  photo={
                    {
                      fileLabel: "Te sorteren",
                      sharePointFileUrl: URL.createObjectURL(fileLog.file),
                    } as FileLog
                  }
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                <SearchComponent
                  editMode={true}
                  label="Selecteer unit"
                  isCleared={() => {
                    handleClearUnit(index);
                  }}
                  onSelect={(resource: any) =>
                    handleUnitSelectForFileLog(resource, index)
                  }
                  resourceFilter={[ResourceType.UNIT]}
                  initialValue={
                    fileLog.fileLog.unitId || (unit && unit.id) || ""
                  }
                  mode={SearchMode.SEARCH_AND_PRESENT}
                  customData={
                    buildingId
                      ? resources.filter(
                          (resource) =>
                            resource.type === ResourceType.UNIT &&
                            resource.resourceId.startsWith(buildingId),
                        )
                      : resources.filter(
                          (resource) => resource.type === ResourceType.UNIT,
                        )
                  } // Show all units if no building is selected
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                <FileLabelSearchComponent
                  key={`${fileLog.fileLog.resourceTypes?.[0]}-${fileLog.fileLog.documentTypes?.[0]}`}
                  incomingSelectedLabel={
                    fileLog.fileLog.fileLabel.label || null
                  }
                  incomingDocumentTypes={fileLog.fileLog.documentTypes || []}
                  incomingResourceTypes={fileLog.fileLog.resourceTypes || []}
                  onSelect={(label: any) =>
                    handleLabelSelectChange(label, index)
                  }
                  labelType="full"
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                <EditableTextareaComponent
                  label=""
                  value={fileLog.fileLog.summary || ""}
                  editMode={true}
                  isLoading={false}
                  onChange={(value) =>
                    handleSummaryChange(value, index, "summary")
                  }
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                <Input
                  label=""
                  type="date"
                  value={(fileLog.fileLog.documentDate as string) || ""}
                  onChange={(e) => handleInputChange(e, index, "documentDate")}
                  classNames={{
                    input:
                      "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
                    inputWrapper:
                      "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                  }}
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2">
                <EditableSelectComponent
                  label=""
                  editMode={true}
                  value={(fileLog.fileLog.status as string) || ""}
                  options={Object.values(FileLogStatus).map((value) => ({
                    label: value,
                    value,
                  }))}
                  onChange={(value) =>
                    handleSelectChange(value, index, "status")
                  }
                />
              </TableCell>
              <TableCell className="min-w-[150px] border p-2 text-center">
                <Tooltip content="Bestand verwijderen">
                  <Trash
                    className="cursor-pointer text-red-500"
                    weight="duotone"
                    size={18}
                    onClick={() => handleDeleteFile(index)}
                  />
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={modalSize as any}
      classNames={{
        wrapper: "sm:p-10 sm:m-10",
        base: "min-h-[80vh]",
      }}
      isDismissable={isDismissable}
      shouldBlockScroll={true}
      scrollBehavior="inside"
      className="p-8"
      onOpenChange={onOpenChange}
    >
      <ModalContent className="text-neutral-primary flex flex-col gap-4 font-normal">
        {!isInit && (
          <div className="bg-opacity-50 absolute inset-0 z-10 flex items-center justify-center bg-black">
            <Spinner
              label={`Uploadsessie voor Gebruiker ${
                user && user.id
              } aan het initialiseren... even geduld...`}
              size="lg"
              color="warning"
              labelColor="warning"
            />
          </div>
        )}
        {/* While a batch runs the form stays blocked - the payloads are already
            built, so edits made now would not be sent - but the overlay shows the
            real progress panel instead of an opaque spinner, so the user can see
            which file is where and how long the rest will take. */}
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
            {uploadQueue.tasks.length > 0 ? (
              <div className="w-full max-w-2xl">
                <UploadProgressPanel
                  tasks={uploadQueue.tasks}
                  summary={uploadQueue.summary}
                  onCancelAll={uploadQueue.cancelAll}
                  defaultCollapsed={uploadQueue.tasks.length > 8}
                />
              </div>
            ) : (
              <Spinner
                label={`${
                  uploadType == UploadType.PICTURES ? "Foto's" : "Bestanden"
                } worden voorbereid... even geduld...`}
                size="lg"
                color="warning"
                labelColor="warning"
              />
            )}
          </div>
        )}
        <ModalHeader className="flex flex-row items-center justify-between p-0 font-normal">
          <h3>
            Upload{" "}
            {uploadType == UploadType.PICTURES ? "Foto's" : "Bestand(en)"}
          </h3>
          <div>
            <ButtonNew
              variant="primaryMoss"
              onClick={() => {
                !isLoading && handleSave();
              }}
              icon={<Checkmark />}
              label="Bewaren"
            />
          </div>
        </ModalHeader>
        <ModalBody className="flex flex-col gap-8 px-0.5">
          {/* After a partly failed batch the overlay is gone but the outcome still
              matters: show which files failed and why, until dismissed. */}
          {!uploadQueue.summary.isRunning &&
            uploadQueue.summary.failed > 0 && (
              <UploadProgressPanel
                tasks={uploadQueue.tasks}
                summary={uploadQueue.summary}
                onDismiss={uploadQueue.reset}
              />
            )}
          <section className="flex flex-col gap-4 md:flex-row md:*:w-full">
            {files &&
            files.length > 0 &&
            !shouldSkipField(FileUploadField.BUILDING) &&
            (!isEpcLabelSelected() ||
              (isEpcLabelSelected() &&
                !selectedUnit &&
                !selectedUnit?.id &&
                !unitId)) &&
            buildings &&
            buildings.length > 0 ? (
              <div className="border-neutral-primary/20 flex flex-col gap-2 rounded-md border p-3">
                <header className="flex items-center gap-1 text-sm font-medium">
                  <Buildings weight="duotone" size={18} />
                  <p>Gebouw</p>
                </header>
                <EditableSelectComponent
                  editMode={true}
                  label="Selecteer gebouw"
                  value={
                    (selectedBuilding && selectedBuilding.id) ||
                    buildingId ||
                    (building && building.id) ||
                    ""
                  }
                  options={buildings.map((buildingId) => ({
                    value: buildingId,
                    label: buildingId,
                  }))}
                  onChange={handleBuildingSelect}
                />
              </div>
            ) : (
              ((!shouldSkipField(FileUploadField.BUILDING) &&
                !isEpcLabelSelected()) ||
                (isEpcLabelSelected() &&
                  !selectedUnit &&
                  !selectedUnit?.id &&
                  !unitId)) && (
                // Keep your existing SearchComponent for building selection when buildings array is not provided or empty
                <div className="border-neutral-primary/20 flex flex-col gap-2 rounded-md border p-3">
                  <header className="flex items-center gap-1 text-sm font-medium">
                    <Buildings weight="duotone" size={18} />
                    <p>Gebouw</p>
                  </header>
                  <SearchComponent
                    editMode={selectedBuilding || building ? false : true}
                    label="Selecteer gebouw"
                    onSelect={handleBuildingSelect}
                    resourceFilter={[ResourceType.BUILDING]}
                    initialValue={
                      (selectedBuilding && selectedBuilding.id) ||
                      buildingId ||
                      (building && building.id) ||
                      ""
                    }
                    mode={SearchMode.SEARCH_AND_PRESENT}
                  />
                </div>
              )
            )}
            {files &&
              files.length == 1 &&
              !shouldSkipField(FileUploadField.UNIT) && (
                <div className="border-neutral-primary/20 flex flex-col gap-2 rounded-md border p-3">
                  <header className="flex items-center gap-1 text-sm font-medium">
                    <StackSimple weight="duotone" size={18} />
                    <p>Unit</p>
                  </header>
                  <SearchComponent
                    editMode={selectedUnit || unit ? false : true}
                    label="Selecteer unit"
                    onSelect={handleUnitSelect}
                    resourceFilter={[ResourceType.UNIT]}
                    initialValue={
                      (selectedUnit && selectedUnit.id) ||
                      unitId ||
                      (unit && unit.id) ||
                      ""
                    }
                    mode={SearchMode.SEARCH_AND_PRESENT}
                    customData={
                      buildingId
                        ? resources.filter(
                            (resource) =>
                              resource.type === ResourceType.UNIT &&
                              resource.resourceId.startsWith(buildingId),
                          )
                        : resources.filter(
                            (resource) => resource.type === ResourceType.UNIT,
                          )
                    } // Show all units if no building is selected
                  />
                </div>
              )}
            {/* Attribuut (garage/parking/berging): alleen zichtbaar zonder unit;
                rendert niets wanneer het gebouw geen attributen heeft. */}
            {files &&
              files.length > 0 &&
              !((selectedUnit && selectedUnit.id) || unitId) && (
                <AttributeSelectSection
                  buildingId={
                    (selectedBuilding && selectedBuilding.id) ||
                    buildingId ||
                    (building && building.id) ||
                    null
                  }
                  value={selectedAttribute?.id ?? null}
                  onSelect={handleAttributeSelect}
                />
              )}
          </section>

          {files.length === 1 &&
            isRentalRelatedDocument(
              (fileLabel as any)?.label || (fileLabel as FileLabel),
            ) &&
            ((selectedUnit && selectedUnit.id) || unitId) && (
              <div className="flex flex-col gap-4">
                <header className="flex items-center gap-1 text-sm font-medium">
                  <FileText weight="duotone" size={18} />
                  <p>Huurcontract</p>
                </header>
                <SearchComponent
                  editMode={true}
                  label="Selecteer contract"
                  onSelect={
                    (isRentalRelatedDocument(
                      (fileLabel as any)?.label || (fileLabel as FileLabel),
                    ) &&
                      handleContractSelect) ||
                    undefined
                  }
                  mode={SearchMode.SEARCH_AND_PRESENT}
                  api={`${customData && customData.rentalContractId ? `${API_URL}/search/rental/contracts/contract/${customData.rentalContractId}` : `${API_URL}/search/rental/unit/${(selectedUnit && selectedUnit.id) || unitId}/contracts`}`}
                />
              </div>
            )}
          <div
            className="border-neutral-primary/20 flex h-screen items-center justify-center rounded-md border-2 border-dashed p-4"
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={handleDrop}
          >
            <input
              id="file-upload"
              onClick={(e) => e.stopPropagation()}
              type="file"
              multiple={true}
              // For file input change
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleFileChange(Array.from(e.target.files));
                }
              }}
              className="hidden"
            />
            {/* <label
                htmlFor="file-upload"
                className="file:font-semibold transition-all ease-in-out file:mr-2 file:cursor-pointer file:rounded-full file:border-0 file:bg-moss-primary file:px-2.5 file:py-1.5 file:text-sm file:text-white hover:opacity-90 cursor-pointer"
              >
                Select Files
              </label> */}

            <label
              htmlFor="file-upload"
              className="text-neutral-primary cursor-pointer text-center text-base"
            >
              {files &&
                files.length > 0 &&
                `${uploadType == UploadType.PICTURES ? "Foto's " : "Bestanden "} geselecteerd: ${files.map((file) => file.name).join(", ")}`}
              {files &&
                files.length == 0 &&
                "Klik hier of sleep bestanden om te starten"}
            </label>
          </div>
          {(buildingId || unitId) && (
            <>
              {files && files.length == 1 && (
                <section className="flex flex-col gap-8">
                  <div className="flex flex-col gap-4">
                    <header className="flex items-center gap-1 text-sm font-medium">
                      <TagSimple weight="duotone" size={18} />
                      <p>
                        {uploadType == UploadType.PICTURES
                          ? "Hoofdlabel foto (wat stelt de foto voor)"
                          : "Bestand label"}
                      </p>
                    </header>
                    <FileLabelSearchComponent
                      incomingSelectedLabel={
                        (fileLabel as any)?.label ||
                        (fileLabel as string) ||
                        null
                      }
                      customLabels={labelSearchOptions}
                      incomingResourceTypes={resourceTypes}
                      incomingDocumentTypes={documentTypes}
                      onSelect={handleFileLabelChange}
                      labelType="full"
                    />
                  </div>
                  <div className="flex flex-col gap-4">
                    {uploadType == UploadType.PICTURES && (
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <TagSimple weight="duotone" size={18} />
                        <p>
                          Secundair label (waar kan of wil je deze foto's voor
                          gebruiken)
                        </p>
                      </header>
                    )}
                    {uploadType == UploadType.PICTURES && (
                      <TagSelector
                        tags={Object.values(SecondaryLabel).map(
                          (label: any) => {
                            return label;
                          },
                        )}
                        selectedTags={secondaryLabels || []}
                        handleTagClick={(tag: any) => {
                          if (
                            secondaryLabels &&
                            secondaryLabels.includes(tag)
                          ) {
                            handleRemoveTag(tag);
                          } else {
                            handleAddTag(tag);
                          }
                        }}
                      />
                    )}
                  </div>
                  <EditableTextareaComponent
                    focus={true}
                    label="Geef een samenvatting van het bestand of de foto (optioneel)"
                    value={
                      fileLogs.length === 1
                        ? fileLogs[0].fileLog.summary || ""
                        : ""
                    }
                    editMode={true}
                    isLoading={false}
                    onChange={(summary) =>
                      handleSummaryChange(summary, 0, "summary")
                    }
                  />

                  {variant == "extended" && isRentalGuarantee() && (
                    <div className="flex flex-col gap-4">
                      <EditableInputComponent
                        label="Huurwaarborg bedrag"
                        value={rentalGuaranteeAmount}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setRentalGuaranteeAmount(value)}
                      />
                      <EditableSelectComponent
                        label="Type huurwaarborg"
                        value={rentalGuaranteeType || ""}
                        options={Object.values(ValidGuaranteeType).map(
                          (value) => ({
                            label: value,
                            value,
                          }),
                        )}
                        editMode={true}
                        onChange={(value) =>
                          setRentalGuaranteeType(value as any)
                        }
                      />
                    </div>
                  )}

                  {isBuildingPlanLabelSelected() &&
                    !shouldSkipField(
                      FileUploadField.DOCUMENT_DIGITAL_COMPATIBILITY,
                    ) &&
                    variant == "extended" && (
                      <div className="flex flex-col gap-4">
                        <EditableSelectComponent
                          label="Document compatibiliteit"
                          value={
                            (fileLogs.length === 1 &&
                              fileLogs[0].fileLog
                                .documentDigitalCompatibility) ||
                            ""
                          }
                          options={Object.values(
                            DocumentDigitalCompatibility,
                          ).map((value) => ({
                            label: value,
                            value,
                          }))}
                          editMode={true}
                          onChange={(value) =>
                            handleSelectChange(
                              value,
                              0,
                              "documentDigitalCompatibility",
                            )
                          }
                        />
                      </div>
                    )}

                  {!shouldSkipField(FileUploadField.SIGN_STATUS) &&
                    variant == "extended" &&
                    (isBuildingOwnershipDocumentSelected(
                      fileLabel as FileLabel,
                    ) ||
                      isRentalContract() ||
                      isTaxationDocumentSelected()) && (
                      <div className="flex flex-col gap-4">
                        <EditableSelectComponent
                          label="Document tekenstatus"
                          value={
                            (fileLogs.length === 1 &&
                              fileLogs[0].fileLog.signStatus) ||
                            ""
                          }
                          options={Object.values(SignStatus).map((value) => ({
                            label: value,
                            value,
                          }))}
                          editMode={true}
                          onChange={(signStatus) =>
                            handleOwnershipDocumentSignStatusSelect(
                              signStatus as any,
                              0,
                            )
                          }
                        />
                      </div>
                    )}

                  {variant == "extended" && isEpcLabelSelected() && (
                    <div className="flex flex-col gap-4">
                      <EditableSelectComponent
                        label="EPC label"
                        value={epcLabel || ""}
                        options={Object.values(EPCLabel).map((value) => ({
                          label: value,
                          value,
                        }))}
                        editMode={true}
                        onChange={(newLabel) => handleEpcLabelSelect(newLabel)}
                      />
                      <EditableInputComponent
                        label="EPC kWh"
                        value={epcKwh}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setEpcKwh(value)}
                      />
                      <EditableInputComponent
                        label="U-waarde muren"
                        value={uValueWalls}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setUValueWalls(value)}
                      />
                      <EditableInputComponent
                        label="Hoeveel extra muurisolatie nodig? (in m²)"
                        value={extraWallInsulationSqm}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setExtraWallInsulationSqm(value)}
                      />
                      <EditableInputComponent
                        label="U-waarde ramen"
                        value={uValueWindows}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setUValueWindows(value)}
                      />
                      <EditableInputComponent
                        label="U-waarde beglazing"
                        value={uValueGlazing}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setUValueGlazing(value)}
                      />
                      <EditableSelectComponent
                        label="Advies m.b.t. ramen"
                        value={extraWindowAdvice || ""}
                        options={Object.values(WindowAdvice).map((value) => ({
                          label: value,
                          value,
                        }))}
                        editMode={true}
                        onChange={(value) =>
                          handleWindowAdviceSelect(value as any)
                        }
                      />
                      <EditableInputComponent
                        label="U-waarde daken"
                        value={uValueRoofs}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setUValueRoofs(value)}
                      />
                      <EditableInputComponent
                        label="Hoeveel extra dakisolatie nodig? (in m²)"
                        value={extraRoofInsulationSqm}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setExtraRoofInsulationSqm(value)}
                      />
                      <EditableInputComponent
                        label="U-waarde vloeren"
                        value={uValueFloors}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setUValueFloors(value as any)}
                      />
                      <EditableInputComponent
                        label="Hoeveel extra vloerisolatie nodig? (in m²)"
                        value={extraFloorInsulationSqm}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) =>
                          setExtraFloorInsulationSqm(value as any)
                        }
                      />
                      <EditableInputComponent
                        label="CO2 uitstoot"
                        value={coEmission}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setCoEmission(value as any)}
                      />
                      <EditableInputComponent
                        label="S-Score"
                        value={sScore}
                        editMode={true}
                        isLoading={false}
                        onChange={(value) => setSScore(value as any)}
                      />
                      <EditableSelectComponent
                        label="Luchtdichtheid"
                        value={airTightness}
                        editMode={true}
                        options={Object.values(AirTightness).map((value) => ({
                          label: value,
                          value,
                        }))}
                        isLoading={false}
                        onChange={(value) =>
                          handleAirTightnessSelect(value as any)
                        }
                      />

                      <EditableSelectComponent
                        label="Koeling en zomercomfort"
                        value={coolingAndSummerComfort}
                        editMode={true}
                        options={Object.values(CoolingAndSummerComfort).map(
                          (value) => ({
                            label: value,
                            value,
                          }),
                        )}
                        isLoading={false}
                        onChange={(value) =>
                          handleCoolingAndSummerComfortSelect(value as any)
                        }
                      />

                      <EditableSelectComponent
                        label="Koeling en zomercomfort installatie"
                        value={coolingAndSummerComfortInstallation}
                        editMode={true}
                        options={Object.values(
                          CoolingAndSummerComfortInstallation,
                        ).map((value) => ({
                          label: value,
                          value,
                        }))}
                        isLoading={false}
                        onChange={(value) =>
                          handleCoolingAndSummerComfortInstallationSelect(
                            value as any,
                          )
                        }
                      />

                      <EditableSelectComponent
                        label="LED verlichting"
                        value={ledLighting}
                        editMode={true}
                        options={Object.values(LedLighting).map((value) => ({
                          label: value,
                          value,
                        }))}
                        isLoading={false}
                        onChange={(value) =>
                          handleLedLightingSelect(value as any)
                        }
                      />

                      <EditableSelectComponent
                        label="Sanitair warm water"
                        value={sanitaryHotWater}
                        editMode={true}
                        options={Object.values(SanitaryHotWater).map(
                          (value) => ({
                            label: value,
                            value,
                          }),
                        )}
                        isLoading={false}
                        onChange={(value) =>
                          handleSanitaryHotWaterSelect(value as any)
                        }
                      />

                      <EditableSelectComponent
                        label="Verwarmingssysteem"
                        value={heatingSystem}
                        editMode={true}
                        options={Object.values(HeatingSystem).map((value) => ({
                          label: value,
                          value,
                        }))}
                        isLoading={false}
                        onChange={(value) =>
                          handleHeatingSystemSelect(value as any)
                        }
                      />

                      <EditableSelectComponent
                        label="Ventilatiesysteem"
                        value={ventilationSystemType}
                        editMode={true}
                        options={Object.values(VentilationSystemType).map(
                          (value) => ({
                            label: value,
                            value,
                          }),
                        )}
                        isLoading={false}
                        onChange={(value) =>
                          handleVentilationSystemTypeSelect(value as any)
                        }
                      />

                      <EditableSelectComponent
                        label="Zonnepanelen"
                        value={solarPanels}
                        editMode={true}
                        options={Object.values(SolarPanels).map((value) => ({
                          label: value,
                          value,
                        }))}
                        isLoading={false}
                        onChange={(value) =>
                          handleSolarPanelsSelect(value as any)
                        }
                      />

                      <EditableSelectComponent
                        label="Zonneboiler"
                        value={solarBoiler}
                        editMode={true}
                        options={Object.values(SolarBoiler).map((value) => ({
                          label: value,
                          value,
                        }))}
                        isLoading={false}
                        onChange={(value) =>
                          handleSolarBoilerSelect(value as any)
                        }
                      />
                    </div>
                  )}
                  {!shouldSkipField(FileUploadField.STATUS) &&
                    variant == "extended" && (
                      <>
                        <div
                          className={`flex flex-col gap-3 rounded-md border p-3`}
                        >
                          <header className="flex items-center gap-2 text-sm font-medium">
                            <span
                              className={`border-neutral-primary/30 inline-block h-5 w-5 rounded-full border ${getStatusColor(
                                fileLogs.length === 1 &&
                                  fileLogs[0].fileLog.status,
                              )}`}
                            />

                            <p>Status document?</p>
                          </header>
                          <EditableSelectComponent
                            label="Status document"
                            value={
                              (fileLogs.length === 1 &&
                                fileLogs[0].fileLog.status) ||
                              FileLogStatus.UNKNOWN
                            }
                            options={Object.values(FileLogStatus).map(
                              (value) => ({
                                label: getLabelForFileLogStatus(value),
                                value,
                              }),
                            )}
                            editMode={true}
                            onChange={(value) =>
                              handleSelectChange(value, 0, "status")
                            }
                          />
                        </div>

                        <EditableTextareaComponent
                          label="Waarom deze document status?"
                          value={
                            (fileLogs.length === 1 &&
                              fileLogs[0].fileLog.statusDescription) ||
                            ""
                          }
                          editMode={true}
                          isLoading={false}
                          onChange={(value) =>
                            handleStatusDescriptionChange(value, 0)
                          }
                        />
                      </>
                    )}

                  {variant == "extended" && isPermitLabelSelected() && (
                    <EditableTextareaComponent
                      label="Vat het doel van de vergunning in enkele zinnen samen"
                      value={permitSummary || ""}
                      editMode={true}
                      isLoading={false}
                      onChange={(value) => setPermitSummary(value)}
                    />
                  )}

                  {variant == "extended" && (
                    <>
                      <Input
                        label={
                          uploadType == UploadType.PICTURES
                            ? "Datum waarop foto is genomen"
                            : "Documentdatum"
                        }
                        type="date"
                        value={
                          (fileLogs.length === 1 &&
                            (fileLogs[0].fileLog.documentDate as string)) ||
                          (uploadType == UploadType.PICTURES &&
                            new Date().toISOString().split("T")[0]) ||
                          ""
                        }
                        placeholder="Kies een datum"
                        onChange={(e) =>
                          handleInputChange(e, 0, "documentDate")
                        }
                        classNames={{
                          input:
                            "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
                          inputWrapper:
                            "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                        }}
                      />
                      {!shouldSkipField(FileUploadField.EXPIRATION_DATE) && (
                        <Input
                          label="Vervaldatum"
                          type="date"
                          value={
                            (fileLogs.length === 1 &&
                              (fileLogs[0].fileLog.expirationDate as string)) ||
                            ""
                          }
                          placeholder="Kies een datum"
                          onChange={(e) =>
                            handleInputChange(e, 0, "expirationDate")
                          }
                          classNames={{
                            input:
                              "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
                            inputWrapper:
                              "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                          }}
                        />
                      )}
                      {!shouldSkipField(FileUploadField.REFERENCE_CODE) && (
                        <Input
                          label="Referentie / Uniek code"
                          value={
                            (fileLogs.length === 1 &&
                              fileLogs[0].fileLog.referenceCode) ||
                            ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, 0, "referenceCode")
                          }
                          classNames={{
                            input:
                              "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
                            inputWrapper:
                              "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                          }}
                        />
                      )}
                    </>
                  )}
                </section>
              )}
              {files &&
                files.length == 1 &&
                !shouldSkipField(FileUploadField.COUNTERPARTIES) &&
                variant == "extended" && (
                  <>
                    <section className="flex flex-col gap-4">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <Link className="h-[18px] w-[18px]" />
                        <p>Relaties</p>
                      </header>
                    </section>
                    <SearchComponent
                      editMode={true}
                      label="Tegenpartij / Relatie 1"
                      onSelect={handleRelationOneSelect}
                      resourceFilter={[
                        ResourceType.COMPANY,
                        ResourceType.CONTACT,
                      ]}
                      initialValue={
                        (selectedFirstCounterparty &&
                          selectedFirstCounterparty.name) ||
                        ""
                      }
                      mode={SearchMode.SEARCH_AND_PRESENT}
                    />
                    <SearchComponent
                      editMode={true}
                      label="Tegenpartij / Relatie 2"
                      onSelect={handleRelationTwoSelect}
                      resourceFilter={[
                        ResourceType.COMPANY,
                        ResourceType.CONTACT,
                      ]}
                      initialValue={""}
                      mode={SearchMode.SEARCH_AND_PRESENT}
                    />
                  </>
                )}

              {files && files.length > 1 && (
                <div className="flex gap-4">
                  <section className="flex flex-col gap-4">
                    <header className="flex items-center gap-1 text-sm font-medium">
                      <TagSimple weight="duotone" size={18} />
                      <p>Bulk selecteer label voor geselecteerde bestanden</p>
                    </header>
                    <FileLabelSearchComponent
                      incomingSelectedLabel={null}
                      customLabels={labelSearchOptions}
                      incomingResourceTypes={["building", "unit"]}
                      incomingDocumentTypes={documentTypes}
                      onSelect={handleBulkLabelChange}
                      labelType="full"
                    />
                  </section>
                  <section className="flex flex-col gap-4">
                    <header className="flex items-center gap-1 text-sm font-medium">
                      <TagSimple weight="duotone" size={18} />
                      <p>Bulk selecteer unit voor geselecteerde bestanden</p>
                    </header>
                    <SearchComponent
                      editMode={true}
                      label="Selecteer unit"
                      onSelect={(resource: any) =>
                        handleBulkUnitSelectForFileLog(resource)
                      }
                      resourceFilter={[ResourceType.UNIT]}
                      initialValue={
                        (selectedUnit && selectedUnit.id) ||
                        (unit && unit.id) ||
                        ""
                      }
                      mode={SearchMode.SEARCH_AND_PRESENT}
                      customData={
                        buildingId
                          ? resources.filter(
                              (resource) =>
                                resource.type === ResourceType.UNIT &&
                                resource.resourceId.startsWith(buildingId),
                            )
                          : resources.filter(
                              (resource) => resource.type === ResourceType.UNIT,
                            )
                      } // Show all units if no building is selected
                    />
                  </section>
                  <div className="flex gap-4">
                    <section className="flex flex-col gap-4">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <TagSimple weight="duotone" size={18} />
                        <p>
                          Bulk selecteer documentdatum voor geselecteerde
                          bestanden
                        </p>
                      </header>
                      <Input
                        type="date"
                        onChange={(e) =>
                          handleBulkDocumentDateChange(e.target.value)
                        }
                        classNames={{
                          input:
                            "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
                          inputWrapper:
                            "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                        }}
                      />
                    </section>
                    <section className="flex flex-col gap-4">
                      <header className="flex items-center gap-1 text-sm font-medium">
                        <TagSimple weight="duotone" size={18} />
                        <p>
                          Bulk selecteer vervaldatum voor geselecteerde
                          bestanden
                        </p>
                      </header>
                      <Input
                        type="date"
                        onChange={(e) =>
                          handleBulkExpirationDateChange(e.target.value)
                        }
                        classNames={{
                          input:
                            "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
                          inputWrapper:
                            "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                        }}
                      />
                    </section>
                  </div>
                  <section className="flex flex-col gap-4">
                    <header className="flex items-center gap-1 text-sm font-medium">
                      <Link className="h-[18px] w-[18px]" />
                      <p>
                        Bulk selecteer relaties voor geselecteerde bestanden
                      </p>
                    </header>
                    <SearchComponent
                      editMode={true}
                      label="Tegenpartij / Relatie"
                      onSelect={handleBulkRelationSelect}
                      resourceFilter={[
                        ResourceType.COMPANY,
                        ResourceType.CONTACT,
                      ]}
                      mode={SearchMode.SEARCH_AND_PRESENT}
                    />
                  </section>
                </div>
              )}

              {files.length > 1 && (
                <section className="flex flex-col gap-4">
                  <header className="flex items-center gap-1 text-sm font-medium">
                    <Info weight="duotone" size={18} />
                    Bestandsinformatie
                  </header>
                  <div className="overflow-x-auto">
                    <Table
                      isHeaderSticky={true}
                      classNames={{
                        base: "max-h-[50vh] overflow-scroll",
                        wrapper: "p-0",
                      }}
                    >
                      {renderColumns()}
                      {renderRows()}
                    </Table>
                  </div>
                </section>
              )}
            </>
          )}
          <Modal
            isOpen={isPdfModalOpen}
            onClose={() => setIsPdfModalOpen(false)}
            size="3xl"
          >
            <ModalContent>
              <PdfPreview fileLog={selectedPdf || ""} />
            </ModalContent>
          </Modal>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalUploadFileLog;
